/* Optimize Minerals — carousel (Swiper) and marquee.

   Swiper is already bundled and loaded globally by layout/theme.liquid, so
   this adds no new dependency. One initialiser drives the carousel sections,
   the Carousel block, and the marquee behind both the Rotating Banner
   section and the Badge marquee block; per-section options ride on a data-op-swiper JSON attribute, so
   the sections stay declarative and there is no per-section JS.

   Markup contract:
     <div data-op-carousel>
       <div class="swiper" data-op-swiper='{"slidesPerView":1.2}'>
         <div class="swiper-wrapper">
           <div class="swiper-slide">…</div>
         </div>
       </div>
       <button data-op-prev>  <button data-op-next>   (optional)
       <div data-op-dots>                              (optional)
     </div>
*/
(function () {
  'use strict';

  var DEFAULTS = {
    slidesPerView: 1.15,
    spaceBetween: 14,
    breakpoints: {
      750: { slidesPerView: 2.2, spaceBetween: 16 },
      990: { slidesPerView: 4, spaceBetween: 18 }
    }
  };

  function merge(base, extra) {
    var out = {};
    Object.keys(base).forEach(function (k) { out[k] = base[k]; });
    Object.keys(extra || {}).forEach(function (k) { out[k] = extra[k]; });
    return out;
  }

  function init(root) {
    if (!root || root.dataset.opCarouselReady === 'true') return;

    var el = root.querySelector('[data-op-swiper]');
    if (!el) return;

    // Swiper loads with `defer`, so it may not be parsed yet on first pass.
    if (typeof window.Swiper === 'undefined') return false;

    root.dataset.opCarouselReady = 'true';

    var opts = {};
    try {
      opts = JSON.parse(el.getAttribute('data-op-swiper') || '{}');
    } catch (e) {
      opts = {};
    }

    var config = merge(DEFAULTS, opts);

    var prev = root.querySelector('[data-op-prev]');
    var next = root.querySelector('[data-op-next]');
    if (prev && next) {
      config.navigation = { prevEl: prev, nextEl: next, disabledClass: 'is-disabled' };
    }

    var dots = root.querySelector('[data-op-dots]');
    if (dots) {
      config.pagination = {
        el: dots,
        clickable: true,
        bulletClass: 'op-carousel-dot',
        bulletActiveClass: 'is-active'
      };
    }

    config.a11y = merge({ enabled: true }, config.a11y || {});
    config.watchOverflow = true;

    // Respect the viewer's motion preference — no momentum, no autoplay.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      config.speed = 0;
      config.autoplay = false;
    }

    var sw = new window.Swiper(el, config);

    /* Swiper measures slide heights at init. Card images that finish loading
       afterwards make the tallest card grow, but the track keeps its original
       height and clips the overflow - a product card came out 508px inside a
       474px slide. Re-measure once the images have settled. */
    var imgs = el.querySelectorAll('img');
    var pending = 0;
    var settle = function () {
      pending -= 1;
      if (pending <= 0 && sw && !sw.destroyed) sw.update();
    };
    imgs.forEach(function (img) {
      if (img.complete) return;
      pending += 1;
      img.addEventListener('load', settle, { once: true });
      img.addEventListener('error', settle, { once: true });
    });
    if (pending === 0 && sw && !sw.destroyed) sw.update();

    /* Fonts, lazy content and editor edits can also change a slide's height. */
    if (typeof ResizeObserver !== 'undefined') {
      var ro = new ResizeObserver(function () {
        if (sw && !sw.destroyed) sw.update();
      });
      var first = el.querySelector('.swiper-slide');
      if (first) ro.observe(first);
    }

    return true;
  }


  /* --- Marquee ------------------------------------------------------------
     The track travels translateX(-50%) of its own width, so it reads as
     continuous only while it holds an even number of identical rows and the
     first half alone covers the container. Two passes of a short badge list
     fall well short of a wide viewport, so clone rows up to an even count that
     does cover it. Cloning into the track rather than animating each row
     separately is what keeps them in step: a row added after the animation
     started would begin its own cycle mid-way and slide over its neighbours. */
  function initMarquee(root) {
    if (!root) return;
    var track = root.querySelector('.op-marquee__track');
    if (!track) return;

    var first = track.querySelector('.op-marquee__row');
    if (!first) return;

    var rowWidth = first.getBoundingClientRect().width;
    if (!rowWidth) return;                       // not laid out yet

    // Rows per half: enough that the leading half never runs out mid-screen.
    var half = Math.ceil(root.clientWidth / rowWidth);
    if (half < 1) half = 1;

    // Rebuilding restarts the animation, so leave a track that already fits
    // alone - a resize that changes nothing should not make the row jump.
    if (root.dataset.opMarqueeHalf === String(half)) return;
    root.dataset.opMarqueeHalf = String(half);

    // Start from the rows the section rendered; clones sized for a previous
    // width would only skew the count.
    track.querySelectorAll('[data-op-marquee-clone]').forEach(function (n) { n.remove(); });

    var rows = track.querySelectorAll('.op-marquee__row');
    for (var i = rows.length; i < half * 2; i++) {
      var copy = first.cloneNode(true);
      copy.setAttribute('aria-hidden', 'true');
      copy.setAttribute('data-op-marquee-clone', '');
      // Duplicated ids and editor handles would confuse both the DOM and the
      // theme editor, so strip them from the copies.
      copy.querySelectorAll('[id]').forEach(function (n) { n.removeAttribute('id'); });
      copy.querySelectorAll('[data-shopify-editor-block]').forEach(function (n) {
        n.removeAttribute('data-shopify-editor-block');
      });
      track.appendChild(copy);
    }

    // Half the track is `half` rows wide, so scaling the duration by it keeps
    // one row per "Loop duration" second whatever the viewport asked for.
    root.style.setProperty('--op-marquee-cycles', half);

    // Rows added mid-cycle change the track width, and with it what -50% means.
    // Restart so the jump happens now rather than as a visible skip.
    track.style.animation = 'none';
    void track.offsetWidth;
    track.style.animation = '';
  }

  function initMarquees(scope) {
    (scope || document).querySelectorAll('[data-op-marquee]').forEach(initMarquee);
  }

  function initAll() {
    var pending = [];
    initMarquees();
    document.querySelectorAll('[data-op-carousel]').forEach(function (root) {
      if (init(root) === false) pending.push(root);
    });
    // Swiper not parsed yet — retry once it is, rather than failing silently.
    if (pending.length) {
      var tries = 0;
      var timer = setInterval(function () {
        tries++;
        if (typeof window.Swiper !== 'undefined') {
          pending.forEach(init);
          clearInterval(timer);
        } else if (tries > 40) {
          clearInterval(timer);
          console.warn('[op] Swiper never loaded; carousels left as static lists.');
        }
      }, 50);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  function rescan(e) {
    var scope = e.target || document;
    scope.querySelectorAll('[data-op-carousel]').forEach(init);
    initMarquees(scope);
  }
  // Icons that decode after first paint change how wide a row is, and so how
  // many it takes to cover the viewport.
  window.addEventListener('load', function () { initMarquees(); });

  // Widening the window can expose the end of a marquee, so top the rows up.
  var resizeTimer;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(initMarquees, 200);
  });

  document.addEventListener('shopify:section:load', rescan);
  // Theme blocks can be added, moved or removed without reloading the section.
  document.addEventListener('shopify:block:select', rescan);
})();
